/**
 * TypeScript types for Annotation Task Management API
 */

// ============================================================================
// Enums
// ============================================================================

export type BatchStatus = 'draft' | 'active' | 'completed' | 'archived';

export type TaskStatus = 'pending' | 'assigned' | 'completed' | 'skipped' | 'disputed';

export type EmotionValence = 'positive' | 'negative' | 'neutral';

export type MessageType =
  | 'substantive'
  | 'greeting'
  | 'confirmation'
  | 'continuation'
  | 'thanks'
  | 'farewell'
  | 'unclear'
  | 'command';

export type ExpectedRouting = 'simple_prompt' | 'full_agent';

export type ConfidenceLevel = 'high' | 'medium' | 'low';

export type SourceType = 'dora_sessions' | 'manual' | 'import';

export type TaskDifficulty = 'easy' | 'medium' | 'hard';

// ============================================================================
// Batch Types
// ============================================================================

export interface AnnotationBatch {
  id: string;
  batch_id: string;
  title: string;
  description?: string | null;
  source_type: string;
  source_query?: Record<string, unknown> | null;
  total_tasks: number;
  completed_tasks: number;
  status: BatchStatus;
  progress_percent: number;
  created_by?: string | null;
  created_at: string;
  updated_at: string;
}

export interface BatchCreate {
  title: string;
  description?: string | null;
  source_type: SourceType;
  source_query?: Record<string, unknown> | null;
}

export interface BatchUpdate {
  title?: string;
  description?: string | null;
}

export interface BatchListResponse {
  batches: AnnotationBatch[];
  total: number;
}

// ============================================================================
// Task Types
// ============================================================================

export interface AnnotationTask {
  id: string;
  task_id: string;
  batch_id?: string | null;
  original_text: string;
  text_hash: string; // MD5 hash for deduplication
  source_session_id?: string | null;
  source_message_index?: number | null;
  status: TaskStatus;
  assigned_to?: string | null;
  assigned_at?: string | null;
  priority: number;
  difficulty?: TaskDifficulty | null;
  created_at: string;
  updated_at: string;
  label?: AnnotationLabel | null;
}

export interface TaskCreate {
  original_text: string;
  batch_id?: string | null;
  source_session_id?: string | null;
  source_message_index?: number | null;
  priority?: number;
  difficulty?: TaskDifficulty | null;
}

export interface TaskAssignRequest {
  user_id?: string | null;
}

export interface TaskListResponse {
  tasks: AnnotationTask[];
  total: number;
}

// ============================================================================
// Label Types
// ============================================================================

export interface AnnotationLabel {
  id: string;
  task_id: string;
  // Message classification
  message_type?: MessageType | null;
  expected_routing?: ExpectedRouting | null;
  // Emotion/Intent (may be null for non-substantive messages)
  emotion_valence?: EmotionValence | null;
  emotion_label?: string | null;
  intents: string[];
  needs_support: boolean;
  // Metadata
  annotator_id: string;
  annotator_notes?: string | null;
  confidence?: ConfidenceLevel | null;
  time_spent_seconds?: number | null;
  submitted_at: string;
}

export interface LabelCreate {
  // Step 1: Message classification (required for all)
  message_type: MessageType;
  expected_routing: ExpectedRouting;
  // Step 2: Emotion/Intent (only for substantive messages)
  emotion_valence?: EmotionValence | null;
  emotion_label?: string | null;
  intents: string[];
  needs_support: boolean;
  // Metadata
  annotator_notes?: string | null;
  confidence?: ConfidenceLevel | null;
  time_spent_seconds?: number | null;
}

// ============================================================================
// Export Types
// ============================================================================

export type UserActivityLevel = 'single' | 'light' | 'medium' | 'heavy';
export type ConversationDepth = 'single_turn' | 'short' | 'medium' | 'deep';
export type MessagePosition = 'first' | 'early' | 'middle' | 'last';
export type UserStage = 'new' | 'recent' | 'active' | 'veteran';

export interface ExportFilters {
  // Time filters
  date_start?: string | null;
  date_end?: string | null;

  // Message content filters
  min_message_length?: number;
  max_message_length?: number;
  exclude_patterns?: string[];
  include_keywords?: string[];

  // Session filters
  session_ids?: string[];

  // User dimension filters
  user_activity_levels?: UserActivityLevel[];
  user_stages?: UserStage[];

  // Conversation dimension filters
  conversation_depths?: ConversationDepth[];
  message_positions?: MessagePosition[];

  // Smart filtering options
  exclude_template_messages?: boolean;
  random_sample?: boolean;
}

export interface ExportPreviewRequest {
  filters?: ExportFilters;
  sample_size?: number;
  offset?: number;
  exclude_exported?: boolean;
}

export interface ExportPreviewItem {
  id: string; // session_id:message_index
  text: string;
  text_hash: string; // MD5 hash for deduplication
  session_id: string;
  message_index: number;
  char_count: number;
  is_duplicate: boolean; // Whether this text already exists in annotation_tasks
  // Metadata for filtering display
  user_activity_level?: UserActivityLevel | null;
  conversation_depth?: ConversationDepth | null;
  user_stage?: UserStage | null;
  message_position?: MessagePosition | null;
}

export interface ExportPreviewResponse {
  total_available: number;
  sample: ExportPreviewItem[];
  filters_applied: Record<string, unknown>;
}

export interface ExportExecuteRequest {
  filters?: ExportFilters;
  sample_size?: number;
  batch_title: string;
  batch_description?: string | null;
  selected_ids?: string[] | null; // Selected message IDs (for manual selection)
  skip_duplicates?: boolean; // Skip messages that already exist based on text_hash
}

export interface ExportExecuteResponse {
  batch_id: string;
  batch_code: string;
  tasks_created: number;
  message: string;
}

// ============================================================================
// Statistics Types
// ============================================================================

export interface AnnotatorStats {
  annotator_id: string;
  total_completed: number;
  total_skipped: number;
  avg_time_seconds?: number | null;
  recent_activity?: string | null;
}

export interface BatchStats {
  batch_id: string;
  title: string;
  total_tasks: number;
  completed_tasks: number;
  pending_tasks: number;
  assigned_tasks: number;
  skipped_tasks: number;
  progress_percent: number;
}

export interface OverviewStats {
  total_batches: number;
  active_batches: number;
  total_tasks: number;
  completed_tasks: number;
  pending_tasks: number;
  total_annotators: number;
  avg_completion_time_seconds?: number | null;
  completion_rate: number;
}

export interface StatsResponse {
  overview: OverviewStats;
  by_batch: BatchStats[];
  by_annotator: AnnotatorStats[];
}

// ============================================================================
// Golden Export Types
// ============================================================================

export interface GoldenCase {
  id: string;
  text: string;
  // Message classification
  message_type: string;
  expected_routing: string;
  // Emotion/Intent (for substantive messages)
  expected_valence?: string | null;
  expected_emotion_label?: string | null;
  expected_intents: string[];
  expected_support: boolean;
  annotation_confidence?: string | null;
}

export interface ClassifierTrainingCase {
  id: string;
  text: string;
  message_type: string;
  expected_routing: string;
}

export interface GoldenExportRequest {
  batch_ids?: string[];
  min_confidence?: ConfidenceLevel | null;
  include_notes?: boolean;
  export_type?: 'emotion' | 'classifier';
}

export interface GoldenExportResponse {
  cases: GoldenCase[];
  classifier_cases: ClassifierTrainingCase[];
  total_cases: number;
  source_batches: string[];
  exported_at: string;
}

// ============================================================================
// Query Parameters
// ============================================================================

export interface BatchListParams {
  status?: BatchStatus;
  limit?: number;
  offset?: number;
}

export interface TaskListParams {
  batch_id?: string;
  status?: TaskStatus;
  assigned_to?: string;
  limit?: number;
  offset?: number;
}

// ============================================================================
// Intent Options (for UI)
// ============================================================================

export const INTENT_OPTIONS = [
  { value: 'astrology', label: '占星咨询' },
  { value: 'fortune_inquiry', label: '运势查询' },
  { value: 'life_domain', label: '生活领域' },
  { value: 'relationship', label: '感情关系' },
  { value: 'career', label: '事业工作' },
  { value: 'health', label: '健康养生' },
  { value: 'support', label: '情感支持' },
  { value: 'greeting', label: '问候寒暄' },
  { value: 'other', label: '其他' },
] as const;

export const EMOTION_LABELS = [
  { value: '焦虑', label: '焦虑' },
  { value: '开心', label: '开心' },
  { value: '平静', label: '平静' },
  { value: '沮丧', label: '沮丧' },
  { value: '期待', label: '期待' },
  { value: '困惑', label: '困惑' },
  { value: '担忧', label: '担忧' },
  { value: '满足', label: '满足' },
  { value: '失落', label: '失落' },
  { value: '好奇', label: '好奇' },
] as const;

// ============================================================================
// Message Type Options (for routing classifier training)
// ============================================================================

export const MESSAGE_TYPE_OPTIONS = [
  {
    value: 'substantive',
    label: '实质性消息',
    description: '需要完整情绪/意图分析的消息',
    routing: 'full_agent' as ExpectedRouting,
  },
  {
    value: 'greeting',
    label: '问候语',
    description: '你好、嗨、早上好等',
    routing: 'simple_prompt' as ExpectedRouting,
  },
  {
    value: 'confirmation',
    label: '确认回复',
    description: '好的、是的、可以、没问题等',
    routing: 'simple_prompt' as ExpectedRouting,
  },
  {
    value: 'continuation',
    label: '续话请求',
    description: '然后?、继续、还有呢、接下来呢等',
    routing: 'simple_prompt' as ExpectedRouting,
  },
  {
    value: 'thanks',
    label: '感谢',
    description: '谢谢、感谢、多谢等',
    routing: 'simple_prompt' as ExpectedRouting,
  },
  {
    value: 'farewell',
    label: '告别',
    description: '再见、拜拜、下次见等',
    routing: 'simple_prompt' as ExpectedRouting,
  },
  {
    value: 'command',
    label: '命令/数据',
    description: '请分析、请预测等指令，或直接粘贴星盘/出生信息等数据',
    routing: 'full_agent' as ExpectedRouting,
  },
  {
    value: 'unclear',
    label: '无法判断',
    description: '混合内容或难以分类',
    routing: 'full_agent' as ExpectedRouting,
  },
] as const;

export const EXPECTED_ROUTING_OPTIONS = [
  {
    value: 'simple_prompt',
    label: '简单提示词',
    description: '使用简单模板回复，无需完整agent处理',
  },
  {
    value: 'full_agent',
    label: '完整Agent',
    description: '需要完整的agent处理流程',
  },
] as const;
